import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer } from "@phosphor-icons/react";
import { rcApi, ytUrl } from "./rcApi";
import { RetourRencontre } from "./rcUi";

const fmtDate = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "");

export default function RythmesFiche() {
  const { numero = "1" } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    document.title = `Fiche — Rencontre ${numero} · Rythmes & Connexions`;
    rcApi.get(`/fiche/${numero}`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Fiche indisponible"));
  }, [numero]);
  if (err) return <div className="min-h-screen bg-white text-zinc-800 p-10 font-manrope"><p data-testid="rc-fiche-error">{err}</p><Link to="/rythmes-connexions" className="underline">Retour</Link></div>;
  if (!d) return <div className="min-h-screen bg-white text-zinc-500 p-10">Chargement…</div>;
  const { settings: s, session: m, resources, tracks, skills, levels, retour_questions, prochaine, ordre_soiree } = d;
  const quand = prochaine ? `${fmtDate(prochaine.date)}${prochaine.heure ? ` · ${prochaine.heure}` : ""}` : (s.prochaine_date ? `${s.prochaine_date}${s.prochaine_heure ? ` · ${s.prochaine_heure}` : ""}` : "Date à confirmer");
  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white font-manrope text-zinc-900" data-testid="rc-fiche-page">
      <style>{`@media print { @page { size: letter; margin: 12mm; } .no-print { display: none !important; } .sheet { box-shadow: none !important; margin: 0 !important; padding: 0 !important; max-width: none !important; } body { background: #fff; } }`}</style>
      <div className="no-print max-w-[8.5in] mx-auto px-4 pt-6 flex items-center justify-between gap-3">
        <Link to="/rythmes/mon-parcours" className="text-sm text-zinc-600 underline">← Mon parcours</Link>
        <button onClick={() => window.print()} data-testid="rc-fiche-print" className="inline-flex items-center gap-2 rounded-full bg-[#0b1220] text-white px-5 py-2.5 text-sm font-bold"><Printer size={18} /> Imprimer / Enregistrer en PDF</button>
      </div>
      <article className="sheet bg-white max-w-[8.5in] mx-auto my-6 p-[0.6in] shadow-lg text-[12.5px] leading-snug">
        <header className="flex items-start justify-between gap-4 border-b-2 border-[#0b1220] pb-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#b8941f]">Rythmes & Connexions – Val-d'Or · Rencontre {m.numero}</div>
            <h1 className="text-2xl font-bold font-cabinet leading-tight mt-1">{m.theme}</h1>
            {m.objectif && <p className="mt-1 text-[13px]"><b>Objectif :</b> {m.objectif}</p>}
          </div>
          <div className="text-right text-[11px] text-zinc-600 shrink-0">
            <div className="font-bold text-zinc-900">{quand}</div>
            <div>{prochaine?.lieu || s.lieu}</div>
            <div className="mt-1">Prénom : ______________________</div>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-x-6 gap-y-2 mb-4">
          {(m.etapes || []).map((e, i) => (
            <div key={i} className="flex gap-2">
              <span className="w-6 h-6 rounded-full bg-[#0b1220] text-white text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
              <div><div className="font-bold">{e.titre}</div><div className="text-zinc-700">{e.consigne}</div></div>
            </div>
          ))}
        </section>

        <div className="grid grid-cols-2 gap-4 mb-4">
          {m.ecoutons && <div className="rounded-lg border border-zinc-300 p-3"><div className="text-[10px] uppercase tracking-widest text-[#b8941f] mb-1">Ce qu'on écoute</div>{m.ecoutons}</div>}
          {m.exercice && <div className="rounded-lg border border-zinc-300 p-3"><div className="text-[10px] uppercase tracking-widest text-[#b8941f] mb-1">Exercice de 5 minutes à la maison</div>{m.exercice}</div>}
        </div>

        {m.retour_rencontre && <RetourRencontre retour={m.retour_rencontre} print />}

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#b8941f] mb-1">Vidéos pour revoir</div>
            <ul className="space-y-1">
              {resources.map((r) => <li key={r.id}><b>{r.titre}</b> <span className="text-zinc-600">— {r.auteur}</span><br /><span className="text-[10.5px] text-zinc-500 break-all">{ytUrl(r.youtube_id)}</span></li>)}
              {!resources.length && <li className="text-zinc-500">Ajoutées prochainement.</li>}
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#b8941f] mb-1">Musique pour pratiquer</div>
            <ul className="space-y-1">
              {tracks.map((t) => <li key={t.id}><b>{t.titre}</b> <span className="text-zinc-600">— {t.artiste}</span>{t.exercice && <span className="text-zinc-500"> · {t.exercice}</span>}</li>)}
              {!tracks.length && <li className="text-zinc-500">La sélection musicale sera ajoutée prochainement.</li>}
            </ul>
          </div>
        </div>

        <section className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-[#b8941f] mb-1">Mon carnet — je coche où j'en suis</div>
          <table className="w-full border-collapse">
            <thead><tr className="text-[10.5px] text-zinc-600"><th className="text-left font-semibold py-1">Compétence</th>{levels.slice(1).map((l) => <th key={l} className="font-semibold w-24">{l}</th>)}</tr></thead>
            <tbody>{skills.map((sk) => <tr key={sk.key} className="border-t border-zinc-200"><td className="py-1.5">{sk.label}</td>{[1, 2, 3].map((lv) => <td key={lv} className="text-center"><span className="inline-block w-4 h-4 border border-zinc-500 rounded-sm" /></td>)}</tr>)}</tbody>
          </table>
        </section>

        <section className="grid grid-cols-3 gap-3 mb-4">
          {retour_questions.map((q) => <div key={q.key}><div className="font-bold mb-1">{q.label}</div><div className="h-14 border-b border-zinc-400" /><div className="h-5 border-b border-zinc-400" /></div>)}
        </section>

        {ordre_soiree?.ordre?.length > 0 && (
          <section className="mb-4 break-inside-avoid" data-testid="rc-fiche-setlist">
            <div className="text-[10px] uppercase tracking-widest text-[#b8941f] mb-1">Table de son — ordre de la danse libre ({ordre_soiree.ordre.length} titres · ≈ {ordre_soiree.estime_min} min)</div>
            <ol className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-[11.5px]">
              {ordre_soiree.ordre.map((o) => <li key={o.n} className="flex gap-1.5"><span className="w-4 text-zinc-500">{o.n}.</span><span><b>{o.titre}</b> <span className="text-zinc-600">— {o.artiste}</span> <span className="text-zinc-500">· {o.label}</span></span></li>)}
            </ol>
          </section>
        )}
        <footer className="border-t border-zinc-300 pt-2 text-[10.5px] text-zinc-600 flex flex-wrap gap-x-4 gap-y-1">
          {(s.regles || []).map((r, i) => <span key={i}>• {r}</span>)}
        </footer>
      </article>
    </div>
  );
}
